"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.authPath = exports.billboardRoot = void 0;
const axios_1 = __importDefault(require("axios"));
const uri_templates_1 = __importDefault(require("uri-templates"));
const GenericCacheTokenHelper_1 = __importDefault(require("./GenericCacheTokenHelper"));
exports.billboardRoot = '/billboard';
exports.authPath = '/AuthServer/api/Token';
class WmsRestClient {
    createAxiosClient;
    cacheTokenHelper;
    constructor(billboardHost, s3BillboardUrl, timeout = 2000, createAxiosClient = axios_1.default.create, cacheTokenHelper = new GenericCacheTokenHelper_1.default()) {
        this.createAxiosClient = createAxiosClient;
        this.cacheTokenHelper = cacheTokenHelper;
        const envTimeOut = process.env.WMS_CLIENT_TIMEOUT;
        if (envTimeOut) {
            try {
                timeout = parseInt(envTimeOut);
            }
            catch {
                console.warn(`Invalid value for WMS_CLIENT_TIMEOUT`);
            }
        }
        this._timeout = timeout;
        this.s3BillboardUrl = s3BillboardUrl;
        this.httpClient = axios_1.default.create({
            baseURL: billboardHost,
            timeout: timeout,
            headers: { Accept: 'application/hal+json' },
        });
    }
    _timeout;
    get timeout() {
        return this._timeout;
    }
    set timeout(value) {
        this._timeout = value;
        this.httpClient.defaults.timeout = value;
    }
    static billboardContents = {};
    static tplGuidToToken = {};
    httpClient;
    s3BillboardUrl;
    wmsHost = '';
    wmsAuthBasic = '';
    async billboardUri(rel) {
        // Parameter checks
        const relVal = rel.Rel.toLowerCase();
        if (relVal === '') {
            throw new Error('Rel property was empty');
        }
        const svcRel = rel.ServiceRel.toLowerCase();
        if (svcRel === '') {
            throw new Error('ServiceRel property was empty');
        }
        const onBillboard = rel.OnBillboard;
        if (!onBillboard) {
            throw new Error(`Rel: '${relVal}' is not on the billboard`);
        }
        // Try to get S3 billboard if no billboard in memory
        if (Object.keys(WmsRestClient.billboardContents).length === 0) {
            // Attempt to pull the billboard from S3 if we haven't
            //   already resolved the billboard;
            await this.getS3Billboard(this.s3BillboardUrl);
        }
        // Try to resolve requested Rel if not found in memory
        if (WmsRestClient.billboardContents[relVal] === undefined) {
            // Resolve specified rel vial WMS REST API
            const svcTemp = await this.getRelFromBillboard(svcRel);
            const serviceRoot = new URL(svcTemp.rootUri).origin;
            const links = svcTemp._links;
            // Fix the href to be fully qualified using rootUri from service
            Object.entries(links).forEach(([, relToAdd]) => {
                const hrefPredicate = relToAdd.href;
                relToAdd.href = `${serviceRoot}${hrefPredicate}`;
            });
            // Add in the adjusted contents of the service billboard
            Object.assign(WmsRestClient.billboardContents, links);
        }
        // Retrieve the desired Rel from memory
        const desiredRel = WmsRestClient.billboardContents[relVal];
        if (desiredRel === undefined) {
            throw new Error(`Could not resolve Rel: '${relVal}'`);
        }
        return desiredRel;
    }
    paramObjToQueryStrings(params) {
        if (!params) {
            return {};
        }
        const result = {};
        for (const [key, value] of Object.entries(params)) {
            if (value !== null && value !== undefined) {
                result[key] = value.toString();
            }
        }
        return result;
    }
    substituteParams(uri, params) {
        const substitutionValues = this.paramObjToQueryStrings(params);
        const template = (0, uri_templates_1.default)(uri);
        const url = template.fill(substitutionValues);
        return url;
    }
    substituteValues(uri, substitutionValues) {
        const template = (0, uri_templates_1.default)(uri);
        const url = template.fill(substitutionValues);
        return url;
    }
    async get(uri, tplGuid, acceptHeader) {
        return this.request('get', uri, undefined, tplGuid, acceptHeader);
    }
    async post(uri, payload, tplGuid, acceptHeader) {
        return this.request('post', uri, payload, tplGuid, acceptHeader);
    }
    async put(uri, payload, tplGuid, acceptHeader) {
        return this.request('put', uri, payload, tplGuid, acceptHeader);
    }
    async delete(uri, tplGuid, acceptHeader) {
        return this.request('delete', uri, undefined, tplGuid, acceptHeader);
    }
    async request(method, uri, payload, tplGuid, acceptHeader) {
        const token = await this.getAuthToken(tplGuid);
        const headers = {
            'Content-Type': 'application/json',
            Accept: acceptHeader ? acceptHeader : 'application/json',
            Authorization: `Bearer ${token}`,
            'Accept-Encoding': 'gzip,deflate,br',
            'Accept-Language': 'en-US,en;q=0.8',
        };
        if (payload && payload['Etag']) {
            headers['If-Match'] = payload['Etag'];
        }
        const formatedUrl = new URL(uri, this.wmsHost);
        const relativePath = `${formatedUrl.pathname}${formatedUrl.search}`;
        const host = formatedUrl.origin;
        const getClient = this.createAxiosClient({
            baseURL: host,
            timeout: this.timeout,
            headers: headers,
        });
        const response = await getClient
            .request({
            method: method,
            url: relativePath,
            data: payload,
        })
            .catch((error) => {
            console.log(`Caught Error From ${method} Request for uri: ${host}${relativePath} tplGuid: ${tplGuid} ` +
                error);
            throw error;
        });
        if (response.headers && 'etag' in response.headers) {
            response.data['Etag'] = response.headers['etag'];
        }
        return response.data;
    }
    async getS3Billboard(s3BillboardUrl) {
        try {
            const response = await axios_1.default.get(s3BillboardUrl);
            if (response.status === 200) {
                WmsRestClient.billboardContents = response.data;
            }
        }
        catch {
            console.log('Caught Error From S3 Billboard Request');
        }
    }
    async getRelFromBillboard(svcRel) {
        const response = await this.httpClient.get(exports.billboardRoot);
        const serviceBillboardHref = response.data._links[svcRel].href;
        const response2 = await this.httpClient.get(serviceBillboardHref);
        return response2.data;
    }
    async getAuthToken(tplGuid) {
        if (!tplGuid) {
            tplGuid = '';
        }
        let token;
        try {
            token = await this.cacheTokenHelper.getToken(tplGuid);
        }
        catch (error) {
            console.warn(`Error trying to get cached token. Error: ${error}`);
        }
        if (token)
            return token;
        const tempRequestBody = {
            grant_type: 'client_credentials',
        };
        if (tplGuid != '') {
            tempRequestBody['tpl'] = tplGuid;
        }
        //don't have a token yet, so get one
        const tokenRequestBody = JSON.stringify(tempRequestBody);
        const authClient = this.createAxiosClient({
            baseURL: this.wmsHost,
            timeout: this.timeout,
            headers: {
                'Content-Type': 'application/json',
                'Content-Length': tokenRequestBody.length,
                Accept: 'application/json',
                Authorization: 'Basic ' + this.wmsAuthBasic,
                'Accept-Encoding': 'gzip,deflate,sdch',
                'Accept-Language': 'en-US,en;q=0.8',
            },
        });
        const response = await authClient
            .post(exports.authPath, tokenRequestBody)
            .catch((error) => {
            console.log(`Caught Error From token Request: ${this.wmsHost}${exports.authPath} ${this.wmsAuthBasic} ` +
                error);
            throw error;
        });
        const newToken = response.data.access_token;
        try {
            await this.cacheTokenHelper.updateToken(tplGuid, newToken);
        }
        catch (error) {
            console.warn(`Error trying to update cached token. Error: ${error}`);
        }
        return newToken;
    }
}
exports.default = WmsRestClient;
